// src/data/golfDefaults.ts
// Default golf tournament content (from the course email + known logistics),
// shared by the public /golf page and the Event Editor:
//   - Golf.tsx uses it as a per-field fallback so an unconfigured event still
//     renders a complete page.
//   - EventEditor.tsx uses it for the "Load default details" button, which
//     pre-fills the form so an admin can tweak it for the year and save.
// Single source of truth — edit here to change the defaults.

export interface GolfCompetition { name: string; note?: string; }
export interface GolfPrize { rank: string; description: string; }
export interface GolfConfig {
  description?: string;
  courseName?: string;
  courseLocation?: string;
  arrivalTime?: string;
  shotgunTime?: string;
  costNote?: string;
  signupNote?: string;
  competitionsIntro?: string;
  competitions?: GolfCompetition[];
  prizesIntro?: string;
  prizes?: GolfPrize[];
  foodNote?: string;
}

// All fields present so callers can rely on them as concrete strings/arrays.
export const DEFAULT_GOLF = {
  description:
    "Kick off WICnic on the course. A morning shotgun-start tournament at Riverdale Dunes " +
    "before the picnic — open to all attendees, with games, prizes, and breakfast on us.",
  courseName: "Riverdale Dunes",
  courseLocation: "Off Riverdale Rd",
  arrivalTime: "7:30 AM",
  shotgunTime: "8:30 AM",
  costNote: "Green fees & carts covered",
  signupNote: "Check the golf box per player",
  competitionsIntro:
    "Riverdale will set up a few skills competitions out on the course. Each has a prize — " +
    "a gift certificate to the course — for the winner.",
  competitions: [
    { name: "Longest Drive", note: "Separate men's and women's competitions" },
    { name: "Closest to the Pin", note: "One shot, closest to the flag" },
    { name: "Long Putt", note: "Sink it from distance" },
  ],
  prizesIntro:
    "Gift certificates to the course go to the top finishers — and to the team that comes in last.",
  prizes: [
    { rank: "1st Place", description: "Gift certificate to the course" },
    { rank: "2nd Place", description: "Gift certificate to the course" },
    { rank: "3rd Place", description: "Gift certificate to the course" },
    { rank: "Last Place", description: "Gift certificate to the course" },
  ],
  foodNote:
    "Breakfast is provided at the start: breakfast burritos (egg, cheese, potato, and pork green chili), " +
    "along with coffee, tea, and orange juice. Players also get drink tokens for use at the course. " +
    "More food and drink details are still being finalized.",
} satisfies Required<GolfConfig>;

/** Deep copy of the defaults — safe to drop into editable form state. */
export function cloneGolfDefaults(): GolfConfig {
  return {
    ...DEFAULT_GOLF,
    competitions: DEFAULT_GOLF.competitions.map(c => ({ ...c })),
    prizes: DEFAULT_GOLF.prizes.map(p => ({ ...p })),
  };
}
